import * as z from 'zod';

export const CreateProjectRequest = z.object({
  project: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Project ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^\d+$/, 'Must be a valid UInt64 string')
          .describe(
            'Revision number, which increments by 1 each time the project is updated. To prevent conflicting changes, the existing revision must be passed when updating the project object.'
          )
          .optional()
          .nullable(),
        title: z
          .string()
          .describe('Project title.')
          .max(100)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe('Project description.')
          .optional()
          .nullable(),
        hidden: z
          .boolean()
          .describe(
            'Whether the project is hidden from the portfolio. Default: `false`'
          )
          .optional()
          .nullable(),
        collectionIds: z.array(z.string()).max(1000).optional(),
        details: z
          .array(
            z.intersection(
              z.object({
                label: z.string().describe('Project label.').optional(),
              }),
              z.xor([
                z.object({
                  text: z.never().optional(),
                  link: z.never().optional(),
                }),
                z.object({
                  link: z.never().optional(),
                  text: z
                    .string()
                    .describe('Project label in plain text format.'),
                }),
                z.object({
                  text: z.never().optional(),
                  link: z
                    .object({
                      text: z
                        .string()
                        .describe('Display text of the link.')
                        .optional()
                        .nullable(),
                      url: z
                        .string()
                        .describe('Target URL of the link.')
                        .url()
                        .optional()
                        .nullable(),
                      target: z
                        .string()
                        .describe(
                          "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Project label in link format.'),
                }),
              ])
            )
          )
          .optional(),
        slug: z.string().describe('Project slug.').optional().nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the project was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the project was updated.')
          .optional()
          .nullable(),
        url: z
          .string()
          .describe(
            'Project page URL and relative path. Returned when `includePageUrl` is `true` in the request.'
          )
          .optional(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('Project SEO data.')
          .optional(),
        watermark: z
          .object({
            position: z
              .enum([
                'NORTH_WEST',
                'NORTH',
                'NORTH_EAST',
                'WEST',
                'CENTER',
                'EAST',
                'SOUTH_WEST',
                'SOUTH',
                'SOUTH_EAST',
              ])
              .optional(),
            size: z
              .number()
              .int()
              .describe('Size of the watermark, from 0 to 100.')
              .min(0)
              .max(100)
              .optional(),
            opacity: z
              .number()
              .int()
              .describe('Opacity of the watermark.')
              .min(1)
              .max(100)
              .optional(),
            imageUrl: z
              .string()
              .describe('Image ID of the Watermark as saved in Media Platform.')
              .max(300)
              .optional(),
            enabled: z
              .boolean()
              .describe(
                'Whether the watermark is to be displayed on Items & Cover Photos.'
              )
              .optional(),
          })
          .describe(
            "Optional watermark that can be applied to all project's images."
          )
          .optional(),
      }),
      z.xor([
        z.object({
          coverImage: z.never().optional(),
          coverVideo: z.never().optional(),
        }),
        z.object({
          coverVideo: z.never().optional(),
          coverImage: z
            .object({
              imageInfo: z
                .string()
                .describe('Information about the Wix Media image.')
                .optional(),
              focalPoint: z
                .object({
                  x: z
                    .number()
                    .describe('X-coordinate of the focal point.')
                    .optional(),
                  y: z
                    .number()
                    .describe('Y-coordinate of the focal point.')
                    .optional(),
                })
                .describe('Focal point of the image.')
                .optional(),
            })
            .describe('Project cover image.'),
        }),
        z.object({
          coverImage: z.never().optional(),
          coverVideo: z
            .object({
              videoInfo: z
                .string()
                .describe('Information about the Wix Media video.')
                .optional(),
              durationInMillis: z
                .number()
                .int()
                .describe('Manually defined Video duration in milliseconds.')
                .optional()
                .nullable(),
            })
            .describe('Project cover video.'),
        }),
      ])
    )
    .describe('Project to create.'),
});
export const CreateProjectResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Project ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^\d+$/, 'Must be a valid UInt64 string')
      .describe(
        'Revision number, which increments by 1 each time the project is updated. To prevent conflicting changes, the existing revision must be passed when updating the project object.'
      )
      .optional()
      .nullable(),
    title: z.string().describe('Project title.').max(100).optional().nullable(),
    description: z
      .string()
      .describe('Project description.')
      .optional()
      .nullable(),
    hidden: z
      .boolean()
      .describe(
        'Whether the project is hidden from the portfolio. Default: `false`'
      )
      .optional()
      .nullable(),
    collectionIds: z.array(z.string()).max(1000).optional(),
    details: z
      .array(
        z.intersection(
          z.object({ label: z.string().describe('Project label.').optional() }),
          z.xor([
            z.object({
              text: z.never().optional(),
              link: z.never().optional(),
            }),
            z.object({
              link: z.never().optional(),
              text: z.string().describe('Project label in plain text format.'),
            }),
            z.object({
              text: z.never().optional(),
              link: z
                .object({
                  text: z
                    .string()
                    .describe('Display text of the link.')
                    .optional()
                    .nullable(),
                  url: z
                    .string()
                    .describe('Target URL of the link.')
                    .url()
                    .optional()
                    .nullable(),
                  target: z
                    .string()
                    .describe(
                      "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Project label in link format.'),
            }),
          ])
        )
      )
      .optional(),
    slug: z.string().describe('Project slug.').optional().nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the project was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the project was updated.')
      .optional()
      .nullable(),
    url: z
      .string()
      .describe(
        'Project page URL and relative path. Returned when `includePageUrl` is `true` in the request.'
      )
      .optional(),
    seoData: z
      .object({
        tags: z
          .array(
            z.object({
              type: z
                .string()
                .describe(
                  'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                )
                .optional(),
              props: z
                .record(z.string(), z.any())
                .describe(
                  'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                )
                .optional()
                .nullable(),
              meta: z
                .record(z.string(), z.any())
                .describe(
                  'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                )
                .optional()
                .nullable(),
              children: z
                .string()
                .describe(
                  'SEO tag inner content. For example, `<title> inner content </title>`.'
                )
                .optional(),
              custom: z
                .boolean()
                .describe(
                  'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                )
                .optional(),
              disabled: z
                .boolean()
                .describe(
                  "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                )
                .optional(),
            })
          )
          .optional(),
        settings: z
          .object({
            preventAutoRedirect: z
              .boolean()
              .describe(
                'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
              )
              .optional(),
            keywords: z
              .array(
                z.object({
                  term: z.string().describe('Keyword value.').optional(),
                  isMain: z
                    .boolean()
                    .describe('Whether the keyword is the main focus keyword.')
                    .optional(),
                  origin: z
                    .string()
                    .describe(
                      'The source that added the keyword terms to the SEO settings.'
                    )
                    .max(1000)
                    .optional()
                    .nullable(),
                })
              )
              .max(5)
              .optional(),
          })
          .describe('SEO general settings.')
          .optional(),
      })
      .describe('Project SEO data.')
      .optional(),
    watermark: z
      .object({
        position: z
          .enum([
            'NORTH_WEST',
            'NORTH',
            'NORTH_EAST',
            'WEST',
            'CENTER',
            'EAST',
            'SOUTH_WEST',
            'SOUTH',
            'SOUTH_EAST',
          ])
          .describe('Position of the watermark on the image.')
          .optional(),
        size: z
          .number()
          .int()
          .describe('Size of the watermark, from 0 to 100.')
          .min(0)
          .max(100)
          .optional(),
        opacity: z
          .number()
          .int()
          .describe('Opacity of the watermark.')
          .min(1)
          .max(100)
          .optional(),
        imageUrl: z
          .string()
          .describe('Image ID of the Watermark as saved in Media Platform.')
          .max(300)
          .optional(),
        enabled: z
          .boolean()
          .describe(
            'Whether the watermark is to be displayed on Items & Cover Photos.'
          )
          .optional(),
      })
      .describe(
        "Optional watermark that can be applied to all project's images."
      )
      .optional(),
  }),
  z.xor([
    z.object({
      coverImage: z.never().optional(),
      coverVideo: z.never().optional(),
    }),
    z.object({
      coverVideo: z.never().optional(),
      coverImage: z
        .object({
          imageInfo: z
            .string()
            .describe('Information about the Wix Media image.')
            .optional(),
          focalPoint: z
            .object({
              x: z
                .number()
                .describe('X-coordinate of the focal point.')
                .optional(),
              y: z
                .number()
                .describe('Y-coordinate of the focal point.')
                .optional(),
            })
            .describe('Focal point of the image.')
            .optional(),
        })
        .describe('Project cover image.'),
    }),
    z.object({
      coverImage: z.never().optional(),
      coverVideo: z
        .object({
          videoInfo: z
            .string()
            .describe('Information about the Wix Media video.')
            .optional(),
          durationInMillis: z
            .number()
            .int()
            .describe('Manually defined Video duration in milliseconds.')
            .optional()
            .nullable(),
        })
        .describe('Project cover video.'),
    }),
  ])
);
export const GetProjectRequest = z.object({
  projectId: z
    .string()
    .describe('ID of the project to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      includePageUrl: z
        .boolean()
        .describe(
          "Whether to include the project's relative path and full URL in the response. Default: `false`"
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const GetProjectResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Project ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^\d+$/, 'Must be a valid UInt64 string')
      .describe(
        'Revision number, which increments by 1 each time the project is updated. To prevent conflicting changes, the existing revision must be passed when updating the project object.'
      )
      .optional()
      .nullable(),
    title: z.string().describe('Project title.').max(100).optional().nullable(),
    description: z
      .string()
      .describe('Project description.')
      .optional()
      .nullable(),
    hidden: z
      .boolean()
      .describe(
        'Whether the project is hidden from the portfolio. Default: `false`'
      )
      .optional()
      .nullable(),
    collectionIds: z.array(z.string()).max(1000).optional(),
    details: z
      .array(
        z.intersection(
          z.object({ label: z.string().describe('Project label.').optional() }),
          z.xor([
            z.object({
              text: z.never().optional(),
              link: z.never().optional(),
            }),
            z.object({
              link: z.never().optional(),
              text: z.string().describe('Project label in plain text format.'),
            }),
            z.object({
              text: z.never().optional(),
              link: z
                .object({
                  text: z
                    .string()
                    .describe('Display text of the link.')
                    .optional()
                    .nullable(),
                  url: z
                    .string()
                    .describe('Target URL of the link.')
                    .url()
                    .optional()
                    .nullable(),
                  target: z
                    .string()
                    .describe(
                      "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Project label in link format.'),
            }),
          ])
        )
      )
      .optional(),
    slug: z.string().describe('Project slug.').optional().nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the project was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the project was updated.')
      .optional()
      .nullable(),
    url: z
      .string()
      .describe(
        'Project page URL and relative path. Returned when `includePageUrl` is `true` in the request.'
      )
      .optional(),
    seoData: z
      .object({
        tags: z
          .array(
            z.object({
              type: z
                .string()
                .describe(
                  'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                )
                .optional(),
              props: z
                .record(z.string(), z.any())
                .describe(
                  'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                )
                .optional()
                .nullable(),
              meta: z
                .record(z.string(), z.any())
                .describe(
                  'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                )
                .optional()
                .nullable(),
              children: z
                .string()
                .describe(
                  'SEO tag inner content. For example, `<title> inner content </title>`.'
                )
                .optional(),
              custom: z
                .boolean()
                .describe(
                  'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                )
                .optional(),
              disabled: z
                .boolean()
                .describe(
                  "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                )
                .optional(),
            })
          )
          .optional(),
        settings: z
          .object({
            preventAutoRedirect: z
              .boolean()
              .describe(
                'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
              )
              .optional(),
            keywords: z
              .array(
                z.object({
                  term: z.string().describe('Keyword value.').optional(),
                  isMain: z
                    .boolean()
                    .describe('Whether the keyword is the main focus keyword.')
                    .optional(),
                  origin: z
                    .string()
                    .describe(
                      'The source that added the keyword terms to the SEO settings.'
                    )
                    .max(1000)
                    .optional()
                    .nullable(),
                })
              )
              .max(5)
              .optional(),
          })
          .describe('SEO general settings.')
          .optional(),
      })
      .describe('Project SEO data.')
      .optional(),
    watermark: z
      .object({
        position: z
          .enum([
            'NORTH_WEST',
            'NORTH',
            'NORTH_EAST',
            'WEST',
            'CENTER',
            'EAST',
            'SOUTH_WEST',
            'SOUTH',
            'SOUTH_EAST',
          ])
          .describe('Position of the watermark on the image.')
          .optional(),
        size: z
          .number()
          .int()
          .describe('Size of the watermark, from 0 to 100.')
          .min(0)
          .max(100)
          .optional(),
        opacity: z
          .number()
          .int()
          .describe('Opacity of the watermark.')
          .min(1)
          .max(100)
          .optional(),
        imageUrl: z
          .string()
          .describe('Image ID of the Watermark as saved in Media Platform.')
          .max(300)
          .optional(),
        enabled: z
          .boolean()
          .describe(
            'Whether the watermark is to be displayed on Items & Cover Photos.'
          )
          .optional(),
      })
      .describe(
        "Optional watermark that can be applied to all project's images."
      )
      .optional(),
  }),
  z.xor([
    z.object({
      coverImage: z.never().optional(),
      coverVideo: z.never().optional(),
    }),
    z.object({
      coverVideo: z.never().optional(),
      coverImage: z
        .object({
          imageInfo: z
            .string()
            .describe('Information about the Wix Media image.')
            .optional(),
          focalPoint: z
            .object({
              x: z
                .number()
                .describe('X-coordinate of the focal point.')
                .optional(),
              y: z
                .number()
                .describe('Y-coordinate of the focal point.')
                .optional(),
            })
            .describe('Focal point of the image.')
            .optional(),
        })
        .describe('Project cover image.'),
    }),
    z.object({
      coverImage: z.never().optional(),
      coverVideo: z
        .object({
          videoInfo: z
            .string()
            .describe('Information about the Wix Media video.')
            .optional(),
          durationInMillis: z
            .number()
            .int()
            .describe('Manually defined Video duration in milliseconds.')
            .optional()
            .nullable(),
        })
        .describe('Project cover video.'),
    }),
  ])
);
export const ListProjectsRequest = z.object({
  options: z
    .object({
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Maximum number of items to return in the results.')
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Projects limit per response is maximum 100, In the first request the cursor is None ?'
        )
        .optional(),
      includePageUrl: z
        .boolean()
        .describe(
          "Whether to include the project's relative path and full URL in the response. Default: `false`"
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const ListProjectsResponse = z.object({
  projects: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Project ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^\d+$/, 'Must be a valid UInt64 string')
            .describe(
              'Revision number, which increments by 1 each time the project is updated. To prevent conflicting changes, the existing revision must be passed when updating the project object.'
            )
            .optional()
            .nullable(),
          title: z
            .string()
            .describe('Project title.')
            .max(100)
            .optional()
            .nullable(),
          description: z
            .string()
            .describe('Project description.')
            .optional()
            .nullable(),
          hidden: z
            .boolean()
            .describe(
              'Whether the project is hidden from the portfolio. Default: `false`'
            )
            .optional()
            .nullable(),
          collectionIds: z.array(z.string()).max(1000).optional(),
          details: z
            .array(
              z.intersection(
                z.object({
                  label: z.string().describe('Project label.').optional(),
                }),
                z.xor([
                  z.object({
                    text: z.never().optional(),
                    link: z.never().optional(),
                  }),
                  z.object({
                    link: z.never().optional(),
                    text: z
                      .string()
                      .describe('Project label in plain text format.'),
                  }),
                  z.object({
                    text: z.never().optional(),
                    link: z
                      .object({
                        text: z
                          .string()
                          .describe('Display text of the link.')
                          .optional()
                          .nullable(),
                        url: z
                          .string()
                          .describe('Target URL of the link.')
                          .url()
                          .optional()
                          .nullable(),
                        target: z
                          .string()
                          .describe(
                            "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Project label in link format.'),
                  }),
                ])
              )
            )
            .optional(),
          slug: z.string().describe('Project slug.').optional().nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the project was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the project was updated.')
            .optional()
            .nullable(),
          url: z
            .string()
            .describe(
              'Project page URL and relative path. Returned when `includePageUrl` is `true` in the request.'
            )
            .optional(),
          seoData: z
            .object({
              tags: z
                .array(
                  z.object({
                    type: z
                      .string()
                      .describe(
                        'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                      )
                      .optional(),
                    props: z
                      .record(z.string(), z.any())
                      .describe(
                        'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                      )
                      .optional()
                      .nullable(),
                    meta: z
                      .record(z.string(), z.any())
                      .describe(
                        'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                      )
                      .optional()
                      .nullable(),
                    children: z
                      .string()
                      .describe(
                        'SEO tag inner content. For example, `<title> inner content </title>`.'
                      )
                      .optional(),
                    custom: z
                      .boolean()
                      .describe(
                        'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                      )
                      .optional(),
                    disabled: z
                      .boolean()
                      .describe(
                        "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                      )
                      .optional(),
                  })
                )
                .optional(),
              settings: z
                .object({
                  preventAutoRedirect: z
                    .boolean()
                    .describe(
                      'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                    )
                    .optional(),
                  keywords: z
                    .array(
                      z.object({
                        term: z.string().describe('Keyword value.').optional(),
                        isMain: z
                          .boolean()
                          .describe(
                            'Whether the keyword is the main focus keyword.'
                          )
                          .optional(),
                        origin: z
                          .string()
                          .describe(
                            'The source that added the keyword terms to the SEO settings.'
                          )
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe('SEO general settings.')
                .optional(),
            })
            .describe('Project SEO data.')
            .optional(),
          watermark: z
            .object({
              position: z
                .enum([
                  'NORTH_WEST',
                  'NORTH',
                  'NORTH_EAST',
                  'WEST',
                  'CENTER',
                  'EAST',
                  'SOUTH_WEST',
                  'SOUTH',
                  'SOUTH_EAST',
                ])
                .describe('Position of the watermark on the image.')
                .optional(),
              size: z
                .number()
                .int()
                .describe('Size of the watermark, from 0 to 100.')
                .min(0)
                .max(100)
                .optional(),
              opacity: z
                .number()
                .int()
                .describe('Opacity of the watermark.')
                .min(1)
                .max(100)
                .optional(),
              imageUrl: z
                .string()
                .describe(
                  'Image ID of the Watermark as saved in Media Platform.'
                )
                .max(300)
                .optional(),
              enabled: z
                .boolean()
                .describe(
                  'Whether the watermark is to be displayed on Items & Cover Photos.'
                )
                .optional(),
            })
            .describe(
              "Optional watermark that can be applied to all project's images."
            )
            .optional(),
        }),
        z.xor([
          z.object({
            coverImage: z.never().optional(),
            coverVideo: z.never().optional(),
          }),
          z.object({
            coverVideo: z.never().optional(),
            coverImage: z
              .object({
                imageInfo: z
                  .string()
                  .describe('Information about the Wix Media image.')
                  .optional(),
                focalPoint: z
                  .object({
                    x: z
                      .number()
                      .describe('X-coordinate of the focal point.')
                      .optional(),
                    y: z
                      .number()
                      .describe('Y-coordinate of the focal point.')
                      .optional(),
                  })
                  .describe('Focal point of the image.')
                  .optional(),
              })
              .describe('Project cover image.'),
          }),
          z.object({
            coverImage: z.never().optional(),
            coverVideo: z
              .object({
                videoInfo: z
                  .string()
                  .describe('Information about the Wix Media video.')
                  .optional(),
                durationInMillis: z
                  .number()
                  .int()
                  .describe('Manually defined Video duration in milliseconds.')
                  .optional()
                  .nullable(),
              })
              .describe('Project cover video.'),
          }),
        ])
      )
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const UpdateProjectRequest = z.object({
  _id: z
    .string()
    .describe('Project ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  project: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Project ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^\d+$/, 'Must be a valid UInt64 string')
          .describe(
            'Revision number, which increments by 1 each time the project is updated. To prevent conflicting changes, the existing revision must be passed when updating the project object.'
          ),
        title: z
          .string()
          .describe('Project title.')
          .max(100)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe('Project description.')
          .optional()
          .nullable(),
        hidden: z
          .boolean()
          .describe(
            'Whether the project is hidden from the portfolio. Default: `false`'
          )
          .optional()
          .nullable(),
        collectionIds: z.array(z.string()).max(1000).optional(),
        details: z
          .array(
            z.intersection(
              z.object({
                label: z.string().describe('Project label.').optional(),
              }),
              z.xor([
                z.object({
                  text: z.never().optional(),
                  link: z.never().optional(),
                }),
                z.object({
                  link: z.never().optional(),
                  text: z
                    .string()
                    .describe('Project label in plain text format.'),
                }),
                z.object({
                  text: z.never().optional(),
                  link: z
                    .object({
                      text: z
                        .string()
                        .describe('Display text of the link.')
                        .optional()
                        .nullable(),
                      url: z
                        .string()
                        .describe('Target URL of the link.')
                        .url()
                        .optional()
                        .nullable(),
                      target: z
                        .string()
                        .describe(
                          "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Project label in link format.'),
                }),
              ])
            )
          )
          .optional(),
        slug: z.string().describe('Project slug.').optional().nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the project was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the project was updated.')
          .optional()
          .nullable(),
        url: z
          .string()
          .describe(
            'Project page URL and relative path. Returned when `includePageUrl` is `true` in the request.'
          )
          .optional(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('Project SEO data.')
          .optional(),
        watermark: z
          .object({
            position: z
              .enum([
                'NORTH_WEST',
                'NORTH',
                'NORTH_EAST',
                'WEST',
                'CENTER',
                'EAST',
                'SOUTH_WEST',
                'SOUTH',
                'SOUTH_EAST',
              ])
              .optional(),
            size: z
              .number()
              .int()
              .describe('Size of the watermark, from 0 to 100.')
              .min(0)
              .max(100)
              .optional(),
            opacity: z
              .number()
              .int()
              .describe('Opacity of the watermark.')
              .min(1)
              .max(100)
              .optional(),
            imageUrl: z
              .string()
              .describe('Image ID of the Watermark as saved in Media Platform.')
              .max(300)
              .optional(),
            enabled: z
              .boolean()
              .describe(
                'Whether the watermark is to be displayed on Items & Cover Photos.'
              )
              .optional(),
          })
          .describe(
            "Optional watermark that can be applied to all project's images."
          )
          .optional(),
      }),
      z.xor([
        z.object({
          coverImage: z.never().optional(),
          coverVideo: z.never().optional(),
        }),
        z.object({
          coverVideo: z.never().optional(),
          coverImage: z
            .object({
              imageInfo: z
                .string()
                .describe('Information about the Wix Media image.')
                .optional(),
              focalPoint: z
                .object({
                  x: z
                    .number()
                    .describe('X-coordinate of the focal point.')
                    .optional(),
                  y: z
                    .number()
                    .describe('Y-coordinate of the focal point.')
                    .optional(),
                })
                .describe('Focal point of the image.')
                .optional(),
            })
            .describe('Project cover image.'),
        }),
        z.object({
          coverImage: z.never().optional(),
          coverVideo: z
            .object({
              videoInfo: z
                .string()
                .describe('Information about the Wix Media video.')
                .optional(),
              durationInMillis: z
                .number()
                .int()
                .describe('Manually defined Video duration in milliseconds.')
                .optional()
                .nullable(),
            })
            .describe('Project cover video.'),
        }),
      ])
    )
    .describe('Project to update.'),
});
export const UpdateProjectResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Project ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^\d+$/, 'Must be a valid UInt64 string')
      .describe(
        'Revision number, which increments by 1 each time the project is updated. To prevent conflicting changes, the existing revision must be passed when updating the project object.'
      )
      .optional()
      .nullable(),
    title: z.string().describe('Project title.').max(100).optional().nullable(),
    description: z
      .string()
      .describe('Project description.')
      .optional()
      .nullable(),
    hidden: z
      .boolean()
      .describe(
        'Whether the project is hidden from the portfolio. Default: `false`'
      )
      .optional()
      .nullable(),
    collectionIds: z.array(z.string()).max(1000).optional(),
    details: z
      .array(
        z.intersection(
          z.object({ label: z.string().describe('Project label.').optional() }),
          z.xor([
            z.object({
              text: z.never().optional(),
              link: z.never().optional(),
            }),
            z.object({
              link: z.never().optional(),
              text: z.string().describe('Project label in plain text format.'),
            }),
            z.object({
              text: z.never().optional(),
              link: z
                .object({
                  text: z
                    .string()
                    .describe('Display text of the link.')
                    .optional()
                    .nullable(),
                  url: z
                    .string()
                    .describe('Target URL of the link.')
                    .url()
                    .optional()
                    .nullable(),
                  target: z
                    .string()
                    .describe(
                      "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Project label in link format.'),
            }),
          ])
        )
      )
      .optional(),
    slug: z.string().describe('Project slug.').optional().nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the project was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the project was updated.')
      .optional()
      .nullable(),
    url: z
      .string()
      .describe(
        'Project page URL and relative path. Returned when `includePageUrl` is `true` in the request.'
      )
      .optional(),
    seoData: z
      .object({
        tags: z
          .array(
            z.object({
              type: z
                .string()
                .describe(
                  'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                )
                .optional(),
              props: z
                .record(z.string(), z.any())
                .describe(
                  'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                )
                .optional()
                .nullable(),
              meta: z
                .record(z.string(), z.any())
                .describe(
                  'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                )
                .optional()
                .nullable(),
              children: z
                .string()
                .describe(
                  'SEO tag inner content. For example, `<title> inner content </title>`.'
                )
                .optional(),
              custom: z
                .boolean()
                .describe(
                  'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                )
                .optional(),
              disabled: z
                .boolean()
                .describe(
                  "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                )
                .optional(),
            })
          )
          .optional(),
        settings: z
          .object({
            preventAutoRedirect: z
              .boolean()
              .describe(
                'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
              )
              .optional(),
            keywords: z
              .array(
                z.object({
                  term: z.string().describe('Keyword value.').optional(),
                  isMain: z
                    .boolean()
                    .describe('Whether the keyword is the main focus keyword.')
                    .optional(),
                  origin: z
                    .string()
                    .describe(
                      'The source that added the keyword terms to the SEO settings.'
                    )
                    .max(1000)
                    .optional()
                    .nullable(),
                })
              )
              .max(5)
              .optional(),
          })
          .describe('SEO general settings.')
          .optional(),
      })
      .describe('Project SEO data.')
      .optional(),
    watermark: z
      .object({
        position: z
          .enum([
            'NORTH_WEST',
            'NORTH',
            'NORTH_EAST',
            'WEST',
            'CENTER',
            'EAST',
            'SOUTH_WEST',
            'SOUTH',
            'SOUTH_EAST',
          ])
          .describe('Position of the watermark on the image.')
          .optional(),
        size: z
          .number()
          .int()
          .describe('Size of the watermark, from 0 to 100.')
          .min(0)
          .max(100)
          .optional(),
        opacity: z
          .number()
          .int()
          .describe('Opacity of the watermark.')
          .min(1)
          .max(100)
          .optional(),
        imageUrl: z
          .string()
          .describe('Image ID of the Watermark as saved in Media Platform.')
          .max(300)
          .optional(),
        enabled: z
          .boolean()
          .describe(
            'Whether the watermark is to be displayed on Items & Cover Photos.'
          )
          .optional(),
      })
      .describe(
        "Optional watermark that can be applied to all project's images."
      )
      .optional(),
  }),
  z.xor([
    z.object({
      coverImage: z.never().optional(),
      coverVideo: z.never().optional(),
    }),
    z.object({
      coverVideo: z.never().optional(),
      coverImage: z
        .object({
          imageInfo: z
            .string()
            .describe('Information about the Wix Media image.')
            .optional(),
          focalPoint: z
            .object({
              x: z
                .number()
                .describe('X-coordinate of the focal point.')
                .optional(),
              y: z
                .number()
                .describe('Y-coordinate of the focal point.')
                .optional(),
            })
            .describe('Focal point of the image.')
            .optional(),
        })
        .describe('Project cover image.'),
    }),
    z.object({
      coverImage: z.never().optional(),
      coverVideo: z
        .object({
          videoInfo: z
            .string()
            .describe('Information about the Wix Media video.')
            .optional(),
          durationInMillis: z
            .number()
            .int()
            .describe('Manually defined Video duration in milliseconds.')
            .optional()
            .nullable(),
        })
        .describe('Project cover video.'),
    }),
  ])
);
export const BulkUpdateProjectsRequest = z.object({
  options: z
    .object({
      projects: z
        .array(
          z.object({
            project: z
              .intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Project ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                  revision: z
                    .string()
                    .regex(/^\d+$/, 'Must be a valid UInt64 string')
                    .describe(
                      'Revision number, which increments by 1 each time the project is updated. To prevent conflicting changes, the existing revision must be passed when updating the project object.'
                    ),
                  title: z
                    .string()
                    .describe('Project title.')
                    .max(100)
                    .optional()
                    .nullable(),
                  description: z
                    .string()
                    .describe('Project description.')
                    .optional()
                    .nullable(),
                  hidden: z
                    .boolean()
                    .describe(
                      'Whether the project is hidden from the portfolio. Default: `false`'
                    )
                    .optional()
                    .nullable(),
                  collectionIds: z.array(z.string()).max(1000).optional(),
                  details: z
                    .array(
                      z.intersection(
                        z.object({
                          label: z
                            .string()
                            .describe('Project label.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            text: z.never().optional(),
                            link: z.never().optional(),
                          }),
                          z.object({
                            link: z.never().optional(),
                            text: z
                              .string()
                              .describe('Project label in plain text format.'),
                          }),
                          z.object({
                            text: z.never().optional(),
                            link: z
                              .object({
                                text: z
                                  .string()
                                  .describe('Display text of the link.')
                                  .optional()
                                  .nullable(),
                                url: z
                                  .string()
                                  .describe('Target URL of the link.')
                                  .url()
                                  .optional()
                                  .nullable(),
                                target: z
                                  .string()
                                  .describe(
                                    "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
                                  )
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Project label in link format.'),
                          }),
                        ])
                      )
                    )
                    .optional(),
                  slug: z
                    .string()
                    .describe('Project slug.')
                    .optional()
                    .nullable(),
                  _createdDate: z
                    .date()
                    .describe('Date and time the project was created.')
                    .optional()
                    .nullable(),
                  _updatedDate: z
                    .date()
                    .describe('Date and time the project was updated.')
                    .optional()
                    .nullable(),
                  url: z
                    .string()
                    .describe(
                      'Project page URL and relative path. Returned when `includePageUrl` is `true` in the request.'
                    )
                    .optional(),
                  seoData: z
                    .object({
                      tags: z
                        .array(
                          z.object({
                            type: z
                              .string()
                              .describe(
                                'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                              )
                              .optional(),
                            props: z
                              .record(z.string(), z.any())
                              .describe(
                                'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                              )
                              .optional()
                              .nullable(),
                            meta: z
                              .record(z.string(), z.any())
                              .describe(
                                'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                              )
                              .optional()
                              .nullable(),
                            children: z
                              .string()
                              .describe(
                                'SEO tag inner content. For example, `<title> inner content </title>`.'
                              )
                              .optional(),
                            custom: z
                              .boolean()
                              .describe(
                                'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                              )
                              .optional(),
                            disabled: z
                              .boolean()
                              .describe(
                                "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                              )
                              .optional(),
                          })
                        )
                        .optional(),
                      settings: z
                        .object({
                          preventAutoRedirect: z
                            .boolean()
                            .describe(
                              'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                            )
                            .optional(),
                          keywords: z
                            .array(
                              z.object({
                                term: z
                                  .string()
                                  .describe('Keyword value.')
                                  .optional(),
                                isMain: z
                                  .boolean()
                                  .describe(
                                    'Whether the keyword is the main focus keyword.'
                                  )
                                  .optional(),
                                origin: z
                                  .string()
                                  .describe(
                                    'The source that added the keyword terms to the SEO settings.'
                                  )
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                              })
                            )
                            .max(5)
                            .optional(),
                        })
                        .describe('SEO general settings.')
                        .optional(),
                    })
                    .describe('Project SEO data.')
                    .optional(),
                  watermark: z
                    .object({
                      position: z
                        .enum([
                          'NORTH_WEST',
                          'NORTH',
                          'NORTH_EAST',
                          'WEST',
                          'CENTER',
                          'EAST',
                          'SOUTH_WEST',
                          'SOUTH',
                          'SOUTH_EAST',
                        ])
                        .optional(),
                      size: z
                        .number()
                        .int()
                        .describe('Size of the watermark, from 0 to 100.')
                        .min(0)
                        .max(100)
                        .optional(),
                      opacity: z
                        .number()
                        .int()
                        .describe('Opacity of the watermark.')
                        .min(1)
                        .max(100)
                        .optional(),
                      imageUrl: z
                        .string()
                        .describe(
                          'Image ID of the Watermark as saved in Media Platform.'
                        )
                        .max(300)
                        .optional(),
                      enabled: z
                        .boolean()
                        .describe(
                          'Whether the watermark is to be displayed on Items & Cover Photos.'
                        )
                        .optional(),
                    })
                    .describe(
                      "Optional watermark that can be applied to all project's images."
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    coverImage: z.never().optional(),
                    coverVideo: z.never().optional(),
                  }),
                  z.object({
                    coverVideo: z.never().optional(),
                    coverImage: z
                      .object({
                        imageInfo: z
                          .string()
                          .describe('Information about the Wix Media image.')
                          .optional(),
                        focalPoint: z
                          .object({
                            x: z
                              .number()
                              .describe('X-coordinate of the focal point.')
                              .optional(),
                            y: z
                              .number()
                              .describe('Y-coordinate of the focal point.')
                              .optional(),
                          })
                          .describe('Focal point of the image.')
                          .optional(),
                      })
                      .describe('Project cover image.'),
                  }),
                  z.object({
                    coverImage: z.never().optional(),
                    coverVideo: z
                      .object({
                        videoInfo: z
                          .string()
                          .describe('Information about the Wix Media video.')
                          .optional(),
                        durationInMillis: z
                          .number()
                          .int()
                          .describe(
                            'Manually defined Video duration in milliseconds.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Project cover video.'),
                  }),
                ])
              )
              .describe('Project to update.'),
          })
        )
        .max(100)
        .optional(),
      returnFullEntity: z
        .boolean()
        .describe(
          'Whether to return the updated projects.\n\nSet to `true` to return the projects in the response.\n\nDefault: `false`'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const BulkUpdateProjectsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe(
            'Information about the updated project.\nIncluding its ID, index in the bulk request and whether it was successfully updated.'
          )
          .optional(),
        project: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Project ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^\d+$/, 'Must be a valid UInt64 string')
                .describe(
                  'Revision number, which increments by 1 each time the project is updated. To prevent conflicting changes, the existing revision must be passed when updating the project object.'
                )
                .optional()
                .nullable(),
              title: z
                .string()
                .describe('Project title.')
                .max(100)
                .optional()
                .nullable(),
              description: z
                .string()
                .describe('Project description.')
                .optional()
                .nullable(),
              hidden: z
                .boolean()
                .describe(
                  'Whether the project is hidden from the portfolio. Default: `false`'
                )
                .optional()
                .nullable(),
              collectionIds: z.array(z.string()).max(1000).optional(),
              details: z
                .array(
                  z.intersection(
                    z.object({
                      label: z.string().describe('Project label.').optional(),
                    }),
                    z.xor([
                      z.object({
                        text: z.never().optional(),
                        link: z.never().optional(),
                      }),
                      z.object({
                        link: z.never().optional(),
                        text: z
                          .string()
                          .describe('Project label in plain text format.'),
                      }),
                      z.object({
                        text: z.never().optional(),
                        link: z
                          .object({
                            text: z
                              .string()
                              .describe('Display text of the link.')
                              .optional()
                              .nullable(),
                            url: z
                              .string()
                              .describe('Target URL of the link.')
                              .url()
                              .optional()
                              .nullable(),
                            target: z
                              .string()
                              .describe(
                                "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
                              )
                              .optional()
                              .nullable(),
                          })
                          .describe('Project label in link format.'),
                      }),
                    ])
                  )
                )
                .optional(),
              slug: z.string().describe('Project slug.').optional().nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the project was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the project was updated.')
                .optional()
                .nullable(),
              url: z
                .string()
                .describe(
                  'Project page URL and relative path. Returned when `includePageUrl` is `true` in the request.'
                )
                .optional(),
              seoData: z
                .object({
                  tags: z
                    .array(
                      z.object({
                        type: z
                          .string()
                          .describe(
                            'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                          )
                          .optional(),
                        props: z
                          .record(z.string(), z.any())
                          .describe(
                            'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                          )
                          .optional()
                          .nullable(),
                        meta: z
                          .record(z.string(), z.any())
                          .describe(
                            'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                          )
                          .optional()
                          .nullable(),
                        children: z
                          .string()
                          .describe(
                            'SEO tag inner content. For example, `<title> inner content </title>`.'
                          )
                          .optional(),
                        custom: z
                          .boolean()
                          .describe(
                            'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                          )
                          .optional(),
                        disabled: z
                          .boolean()
                          .describe(
                            "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  settings: z
                    .object({
                      preventAutoRedirect: z
                        .boolean()
                        .describe(
                          'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                        )
                        .optional(),
                      keywords: z
                        .array(
                          z.object({
                            term: z
                              .string()
                              .describe('Keyword value.')
                              .optional(),
                            isMain: z
                              .boolean()
                              .describe(
                                'Whether the keyword is the main focus keyword.'
                              )
                              .optional(),
                            origin: z
                              .string()
                              .describe(
                                'The source that added the keyword terms to the SEO settings.'
                              )
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(5)
                        .optional(),
                    })
                    .describe('SEO general settings.')
                    .optional(),
                })
                .describe('Project SEO data.')
                .optional(),
              watermark: z
                .object({
                  position: z
                    .enum([
                      'NORTH_WEST',
                      'NORTH',
                      'NORTH_EAST',
                      'WEST',
                      'CENTER',
                      'EAST',
                      'SOUTH_WEST',
                      'SOUTH',
                      'SOUTH_EAST',
                    ])
                    .describe('Position of the watermark on the image.')
                    .optional(),
                  size: z
                    .number()
                    .int()
                    .describe('Size of the watermark, from 0 to 100.')
                    .min(0)
                    .max(100)
                    .optional(),
                  opacity: z
                    .number()
                    .int()
                    .describe('Opacity of the watermark.')
                    .min(1)
                    .max(100)
                    .optional(),
                  imageUrl: z
                    .string()
                    .describe(
                      'Image ID of the Watermark as saved in Media Platform.'
                    )
                    .max(300)
                    .optional(),
                  enabled: z
                    .boolean()
                    .describe(
                      'Whether the watermark is to be displayed on Items & Cover Photos.'
                    )
                    .optional(),
                })
                .describe(
                  "Optional watermark that can be applied to all project's images."
                )
                .optional(),
            }),
            z.xor([
              z.object({
                coverImage: z.never().optional(),
                coverVideo: z.never().optional(),
              }),
              z.object({
                coverVideo: z.never().optional(),
                coverImage: z
                  .object({
                    imageInfo: z
                      .string()
                      .describe('Information about the Wix Media image.')
                      .optional(),
                    focalPoint: z
                      .object({
                        x: z
                          .number()
                          .describe('X-coordinate of the focal point.')
                          .optional(),
                        y: z
                          .number()
                          .describe('Y-coordinate of the focal point.')
                          .optional(),
                      })
                      .describe('Focal point of the image.')
                      .optional(),
                  })
                  .describe('Project cover image.'),
              }),
              z.object({
                coverImage: z.never().optional(),
                coverVideo: z
                  .object({
                    videoInfo: z
                      .string()
                      .describe('Information about the Wix Media video.')
                      .optional(),
                    durationInMillis: z
                      .number()
                      .int()
                      .describe(
                        'Manually defined Video duration in milliseconds.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Project cover video.'),
              }),
            ])
          )
          .describe(
            'Updated project. Only returned if `returnEntity` is set to `true` in the request.'
          )
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe(
      'Total number of successes and failures for Bulk Update Projects.'
    )
    .optional(),
});
export const DeleteProjectRequest = z.object({
  projectId: z
    .string()
    .describe('ID of the project to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteProjectResponse = z.object({
  projectId: z
    .string()
    .describe('ID of the deleted project.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const QueryProjectsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          title: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          description: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          slug: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          collectionIds: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          hidden: z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_id',
                'title',
                'description',
                'slug',
                '_createdDate',
                '_updatedDate',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
  options: z
    .object({
      includePageUrl: z
        .boolean()
        .describe(
          "Whether to include the project's relative path and full URL in the response. Default: `false`"
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const QueryProjectsResponse = z.object({
  projects: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Project ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^\d+$/, 'Must be a valid UInt64 string')
            .describe(
              'Revision number, which increments by 1 each time the project is updated. To prevent conflicting changes, the existing revision must be passed when updating the project object.'
            )
            .optional()
            .nullable(),
          title: z
            .string()
            .describe('Project title.')
            .max(100)
            .optional()
            .nullable(),
          description: z
            .string()
            .describe('Project description.')
            .optional()
            .nullable(),
          hidden: z
            .boolean()
            .describe(
              'Whether the project is hidden from the portfolio. Default: `false`'
            )
            .optional()
            .nullable(),
          collectionIds: z.array(z.string()).max(1000).optional(),
          details: z
            .array(
              z.intersection(
                z.object({
                  label: z.string().describe('Project label.').optional(),
                }),
                z.xor([
                  z.object({
                    text: z.never().optional(),
                    link: z.never().optional(),
                  }),
                  z.object({
                    link: z.never().optional(),
                    text: z
                      .string()
                      .describe('Project label in plain text format.'),
                  }),
                  z.object({
                    text: z.never().optional(),
                    link: z
                      .object({
                        text: z
                          .string()
                          .describe('Display text of the link.')
                          .optional()
                          .nullable(),
                        url: z
                          .string()
                          .describe('Target URL of the link.')
                          .url()
                          .optional()
                          .nullable(),
                        target: z
                          .string()
                          .describe(
                            "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Project label in link format.'),
                  }),
                ])
              )
            )
            .optional(),
          slug: z.string().describe('Project slug.').optional().nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the project was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the project was updated.')
            .optional()
            .nullable(),
          url: z
            .string()
            .describe(
              'Project page URL and relative path. Returned when `includePageUrl` is `true` in the request.'
            )
            .optional(),
          seoData: z
            .object({
              tags: z
                .array(
                  z.object({
                    type: z
                      .string()
                      .describe(
                        'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                      )
                      .optional(),
                    props: z
                      .record(z.string(), z.any())
                      .describe(
                        'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                      )
                      .optional()
                      .nullable(),
                    meta: z
                      .record(z.string(), z.any())
                      .describe(
                        'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                      )
                      .optional()
                      .nullable(),
                    children: z
                      .string()
                      .describe(
                        'SEO tag inner content. For example, `<title> inner content </title>`.'
                      )
                      .optional(),
                    custom: z
                      .boolean()
                      .describe(
                        'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                      )
                      .optional(),
                    disabled: z
                      .boolean()
                      .describe(
                        "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                      )
                      .optional(),
                  })
                )
                .optional(),
              settings: z
                .object({
                  preventAutoRedirect: z
                    .boolean()
                    .describe(
                      'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                    )
                    .optional(),
                  keywords: z
                    .array(
                      z.object({
                        term: z.string().describe('Keyword value.').optional(),
                        isMain: z
                          .boolean()
                          .describe(
                            'Whether the keyword is the main focus keyword.'
                          )
                          .optional(),
                        origin: z
                          .string()
                          .describe(
                            'The source that added the keyword terms to the SEO settings.'
                          )
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe('SEO general settings.')
                .optional(),
            })
            .describe('Project SEO data.')
            .optional(),
          watermark: z
            .object({
              position: z
                .enum([
                  'NORTH_WEST',
                  'NORTH',
                  'NORTH_EAST',
                  'WEST',
                  'CENTER',
                  'EAST',
                  'SOUTH_WEST',
                  'SOUTH',
                  'SOUTH_EAST',
                ])
                .describe('Position of the watermark on the image.')
                .optional(),
              size: z
                .number()
                .int()
                .describe('Size of the watermark, from 0 to 100.')
                .min(0)
                .max(100)
                .optional(),
              opacity: z
                .number()
                .int()
                .describe('Opacity of the watermark.')
                .min(1)
                .max(100)
                .optional(),
              imageUrl: z
                .string()
                .describe(
                  'Image ID of the Watermark as saved in Media Platform.'
                )
                .max(300)
                .optional(),
              enabled: z
                .boolean()
                .describe(
                  'Whether the watermark is to be displayed on Items & Cover Photos.'
                )
                .optional(),
            })
            .describe(
              "Optional watermark that can be applied to all project's images."
            )
            .optional(),
        }),
        z.xor([
          z.object({
            coverImage: z.never().optional(),
            coverVideo: z.never().optional(),
          }),
          z.object({
            coverVideo: z.never().optional(),
            coverImage: z
              .object({
                imageInfo: z
                  .string()
                  .describe('Information about the Wix Media image.')
                  .optional(),
                focalPoint: z
                  .object({
                    x: z
                      .number()
                      .describe('X-coordinate of the focal point.')
                      .optional(),
                    y: z
                      .number()
                      .describe('Y-coordinate of the focal point.')
                      .optional(),
                  })
                  .describe('Focal point of the image.')
                  .optional(),
              })
              .describe('Project cover image.'),
          }),
          z.object({
            coverImage: z.never().optional(),
            coverVideo: z
              .object({
                videoInfo: z
                  .string()
                  .describe('Information about the Wix Media video.')
                  .optional(),
                durationInMillis: z
                  .number()
                  .int()
                  .describe('Manually defined Video duration in milliseconds.')
                  .optional()
                  .nullable(),
              })
              .describe('Project cover video.'),
          }),
        ])
      )
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const UpdateProjectOrderInCollectionRequest = z.object({
  identifiers: z.object({
    projectId: z
      .string()
      .describe('ID of the project to update.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    collectionId: z
      .string()
      .describe('Collection ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
  }),
  sortOrder: z
    .number()
    .describe(
      'Index that determines the placement of a project within the collection.'
    ),
});
export const UpdateProjectOrderInCollectionResponse = z.object({
  projectInCollection: z
    .object({
      collectionId: z
        .string()
        .describe('Collection ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      project: z
        .intersection(
          z.object({
            _id: z
              .string()
              .describe('Project ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^\d+$/, 'Must be a valid UInt64 string')
              .describe(
                'Revision number, which increments by 1 each time the project is updated. To prevent conflicting changes, the existing revision must be passed when updating the project object.'
              )
              .optional()
              .nullable(),
            title: z
              .string()
              .describe('Project title.')
              .max(100)
              .optional()
              .nullable(),
            description: z
              .string()
              .describe('Project description.')
              .optional()
              .nullable(),
            hidden: z
              .boolean()
              .describe(
                'Whether the project is hidden from the portfolio. Default: `false`'
              )
              .optional()
              .nullable(),
            collectionIds: z.array(z.string()).max(1000).optional(),
            details: z
              .array(
                z.intersection(
                  z.object({
                    label: z.string().describe('Project label.').optional(),
                  }),
                  z.xor([
                    z.object({
                      text: z.never().optional(),
                      link: z.never().optional(),
                    }),
                    z.object({
                      link: z.never().optional(),
                      text: z
                        .string()
                        .describe('Project label in plain text format.'),
                    }),
                    z.object({
                      text: z.never().optional(),
                      link: z
                        .object({
                          text: z
                            .string()
                            .describe('Display text of the link.')
                            .optional()
                            .nullable(),
                          url: z
                            .string()
                            .describe('Target URL of the link.')
                            .url()
                            .optional()
                            .nullable(),
                          target: z
                            .string()
                            .describe(
                              "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe('Project label in link format.'),
                    }),
                  ])
                )
              )
              .optional(),
            slug: z.string().describe('Project slug.').optional().nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the project was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the project was updated.')
              .optional()
              .nullable(),
            url: z
              .string()
              .describe(
                'Project page URL and relative path. Returned when `includePageUrl` is `true` in the request.'
              )
              .optional(),
            seoData: z
              .object({
                tags: z
                  .array(
                    z.object({
                      type: z
                        .string()
                        .describe(
                          'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                        )
                        .optional(),
                      props: z
                        .record(z.string(), z.any())
                        .describe(
                          'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                        )
                        .optional()
                        .nullable(),
                      meta: z
                        .record(z.string(), z.any())
                        .describe(
                          'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                        )
                        .optional()
                        .nullable(),
                      children: z
                        .string()
                        .describe(
                          'SEO tag inner content. For example, `<title> inner content </title>`.'
                        )
                        .optional(),
                      custom: z
                        .boolean()
                        .describe(
                          'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                        )
                        .optional(),
                      disabled: z
                        .boolean()
                        .describe(
                          "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                settings: z
                  .object({
                    preventAutoRedirect: z
                      .boolean()
                      .describe(
                        'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                      )
                      .optional(),
                    keywords: z
                      .array(
                        z.object({
                          term: z
                            .string()
                            .describe('Keyword value.')
                            .optional(),
                          isMain: z
                            .boolean()
                            .describe(
                              'Whether the keyword is the main focus keyword.'
                            )
                            .optional(),
                          origin: z
                            .string()
                            .describe(
                              'The source that added the keyword terms to the SEO settings.'
                            )
                            .max(1000)
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(5)
                      .optional(),
                  })
                  .describe('SEO general settings.')
                  .optional(),
              })
              .describe('Project SEO data.')
              .optional(),
            watermark: z
              .object({
                position: z
                  .enum([
                    'NORTH_WEST',
                    'NORTH',
                    'NORTH_EAST',
                    'WEST',
                    'CENTER',
                    'EAST',
                    'SOUTH_WEST',
                    'SOUTH',
                    'SOUTH_EAST',
                  ])
                  .describe('Position of the watermark on the image.')
                  .optional(),
                size: z
                  .number()
                  .int()
                  .describe('Size of the watermark, from 0 to 100.')
                  .min(0)
                  .max(100)
                  .optional(),
                opacity: z
                  .number()
                  .int()
                  .describe('Opacity of the watermark.')
                  .min(1)
                  .max(100)
                  .optional(),
                imageUrl: z
                  .string()
                  .describe(
                    'Image ID of the Watermark as saved in Media Platform.'
                  )
                  .max(300)
                  .optional(),
                enabled: z
                  .boolean()
                  .describe(
                    'Whether the watermark is to be displayed on Items & Cover Photos.'
                  )
                  .optional(),
              })
              .describe(
                "Optional watermark that can be applied to all project's images."
              )
              .optional(),
          }),
          z.xor([
            z.object({
              coverImage: z.never().optional(),
              coverVideo: z.never().optional(),
            }),
            z.object({
              coverVideo: z.never().optional(),
              coverImage: z
                .object({
                  imageInfo: z
                    .string()
                    .describe('Information about the Wix Media image.')
                    .optional(),
                  focalPoint: z
                    .object({
                      x: z
                        .number()
                        .describe('X-coordinate of the focal point.')
                        .optional(),
                      y: z
                        .number()
                        .describe('Y-coordinate of the focal point.')
                        .optional(),
                    })
                    .describe('Focal point of the image.')
                    .optional(),
                })
                .describe('Project cover image.'),
            }),
            z.object({
              coverImage: z.never().optional(),
              coverVideo: z
                .object({
                  videoInfo: z
                    .string()
                    .describe('Information about the Wix Media video.')
                    .optional(),
                  durationInMillis: z
                    .number()
                    .int()
                    .describe(
                      'Manually defined Video duration in milliseconds.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Project cover video.'),
            }),
          ])
        )
        .describe('Project object.')
        .optional(),
      sortOrder: z
        .number()
        .describe(
          'Index that determines the placement of a project within the collection.  <br />\n\nDefault: [Epoch](https://www.epoch101.com/) timestamp. <br />'
        )
        .optional()
        .nullable(),
      _id: z
        .string()
        .describe('Project placement ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .describe('Updated project placement within the specified collection.')
    .optional(),
});
export const QueryProjectsWithCollectionInfoRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .optional(),
        fields: z.array(z.string()).optional(),
        fieldsets: z.array(z.string()).optional(),
      }),
      z.xor([
        z.object({
          paging: z.never().optional(),
          cursorPaging: z.never().optional(),
        }),
        z.object({
          cursorPaging: z.never().optional(),
          paging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Number of items to load.')
                .min(0)
                .optional()
                .nullable(),
              offset: z
                .number()
                .int()
                .describe('Number of items to skip in the current sort order.')
                .min(0)
                .optional()
                .nullable(),
            })
            .describe(
              'Paging options to limit and offset the number of items.'
            ),
        }),
        z.object({
          paging: z.never().optional(),
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('WQL expression'),
  options: z
    .object({
      includePageUrl: z
        .boolean()
        .describe('Include page url')
        .optional()
        .nullable(),
    })
    .optional(),
});
export const QueryProjectsWithCollectionInfoResponse = z.object({
  projects: z
    .array(
      z.object({
        collectionId: z
          .string()
          .describe('Collection ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        project: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Project ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^\d+$/, 'Must be a valid UInt64 string')
                .describe(
                  'Revision number, which increments by 1 each time the project is updated. To prevent conflicting changes, the existing revision must be passed when updating the project object.'
                )
                .optional()
                .nullable(),
              title: z
                .string()
                .describe('Project title.')
                .max(100)
                .optional()
                .nullable(),
              description: z
                .string()
                .describe('Project description.')
                .optional()
                .nullable(),
              hidden: z
                .boolean()
                .describe(
                  'Whether the project is hidden from the portfolio. Default: `false`'
                )
                .optional()
                .nullable(),
              collectionIds: z.array(z.string()).max(1000).optional(),
              details: z
                .array(
                  z.intersection(
                    z.object({
                      label: z.string().describe('Project label.').optional(),
                    }),
                    z.xor([
                      z.object({
                        text: z.never().optional(),
                        link: z.never().optional(),
                      }),
                      z.object({
                        link: z.never().optional(),
                        text: z
                          .string()
                          .describe('Project label in plain text format.'),
                      }),
                      z.object({
                        text: z.never().optional(),
                        link: z
                          .object({
                            text: z
                              .string()
                              .describe('Display text of the link.')
                              .optional()
                              .nullable(),
                            url: z
                              .string()
                              .describe('Target URL of the link.')
                              .url()
                              .optional()
                              .nullable(),
                            target: z
                              .string()
                              .describe(
                                "Whether the link opens in a new tab or window. One of:\n* `'_blank'`: The link opens in a new tab or window.\n* `'_self'`: The link opens in the same tab or window."
                              )
                              .optional()
                              .nullable(),
                          })
                          .describe('Project label in link format.'),
                      }),
                    ])
                  )
                )
                .optional(),
              slug: z.string().describe('Project slug.').optional().nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the project was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the project was updated.')
                .optional()
                .nullable(),
              url: z
                .string()
                .describe(
                  'Project page URL and relative path. Returned when `includePageUrl` is `true` in the request.'
                )
                .optional(),
              seoData: z
                .object({
                  tags: z
                    .array(
                      z.object({
                        type: z
                          .string()
                          .describe(
                            'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                          )
                          .optional(),
                        props: z
                          .record(z.string(), z.any())
                          .describe(
                            'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                          )
                          .optional()
                          .nullable(),
                        meta: z
                          .record(z.string(), z.any())
                          .describe(
                            'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                          )
                          .optional()
                          .nullable(),
                        children: z
                          .string()
                          .describe(
                            'SEO tag inner content. For example, `<title> inner content </title>`.'
                          )
                          .optional(),
                        custom: z
                          .boolean()
                          .describe(
                            'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                          )
                          .optional(),
                        disabled: z
                          .boolean()
                          .describe(
                            "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  settings: z
                    .object({
                      preventAutoRedirect: z
                        .boolean()
                        .describe(
                          'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                        )
                        .optional(),
                      keywords: z
                        .array(
                          z.object({
                            term: z
                              .string()
                              .describe('Keyword value.')
                              .optional(),
                            isMain: z
                              .boolean()
                              .describe(
                                'Whether the keyword is the main focus keyword.'
                              )
                              .optional(),
                            origin: z
                              .string()
                              .describe(
                                'The source that added the keyword terms to the SEO settings.'
                              )
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(5)
                        .optional(),
                    })
                    .describe('SEO general settings.')
                    .optional(),
                })
                .describe('Project SEO data.')
                .optional(),
              watermark: z
                .object({
                  position: z
                    .enum([
                      'NORTH_WEST',
                      'NORTH',
                      'NORTH_EAST',
                      'WEST',
                      'CENTER',
                      'EAST',
                      'SOUTH_WEST',
                      'SOUTH',
                      'SOUTH_EAST',
                    ])
                    .describe('Position of the watermark on the image.')
                    .optional(),
                  size: z
                    .number()
                    .int()
                    .describe('Size of the watermark, from 0 to 100.')
                    .min(0)
                    .max(100)
                    .optional(),
                  opacity: z
                    .number()
                    .int()
                    .describe('Opacity of the watermark.')
                    .min(1)
                    .max(100)
                    .optional(),
                  imageUrl: z
                    .string()
                    .describe(
                      'Image ID of the Watermark as saved in Media Platform.'
                    )
                    .max(300)
                    .optional(),
                  enabled: z
                    .boolean()
                    .describe(
                      'Whether the watermark is to be displayed on Items & Cover Photos.'
                    )
                    .optional(),
                })
                .describe(
                  "Optional watermark that can be applied to all project's images."
                )
                .optional(),
            }),
            z.xor([
              z.object({
                coverImage: z.never().optional(),
                coverVideo: z.never().optional(),
              }),
              z.object({
                coverVideo: z.never().optional(),
                coverImage: z
                  .object({
                    imageInfo: z
                      .string()
                      .describe('Information about the Wix Media image.')
                      .optional(),
                    focalPoint: z
                      .object({
                        x: z
                          .number()
                          .describe('X-coordinate of the focal point.')
                          .optional(),
                        y: z
                          .number()
                          .describe('Y-coordinate of the focal point.')
                          .optional(),
                      })
                      .describe('Focal point of the image.')
                      .optional(),
                  })
                  .describe('Project cover image.'),
              }),
              z.object({
                coverImage: z.never().optional(),
                coverVideo: z
                  .object({
                    videoInfo: z
                      .string()
                      .describe('Information about the Wix Media video.')
                      .optional(),
                    durationInMillis: z
                      .number()
                      .int()
                      .describe(
                        'Manually defined Video duration in milliseconds.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Project cover video.'),
              }),
            ])
          )
          .describe('Project object.')
          .optional(),
        sortOrder: z
          .number()
          .describe(
            'Index that determines the placement of a project within the collection.  <br />\n\nDefault: [Epoch](https://www.epoch101.com/) timestamp. <br />'
          )
          .optional()
          .nullable(),
        _id: z
          .string()
          .describe('Project placement ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Paging metadata')
    .optional(),
});
